import React, { useEffect, useState } from 'react'
import { TextField, Button, Autocomplete, InputLabel, Select, MenuItem, Chip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add';
import DataService from '../../../../../common/services/dataService';
import SelectedSearchOption, { groupFor } from './SelectedSearchOption';

const RESULTS_PER_PAGE = 25

export default function StimSearchTableTop(props) {

    const { getStimBrands, getStimLanguages, getStimCountries, getStimAdTypeGroups } = DataService

    const { createdFilteredStimList, textFieldChange } = props

    const [searchOptions, setSearchOption] = useState([])

    const [showTextSearch, setShowTextSearch] = useState(false)
    const [fieldOptions, setFieldOptions] = useState([])

    // The filters currently applied, each { searchParam, group, filter, label }. Held here
    // rather than in SelectedSearchOption so the set survives switching search type.
    const [appliedFilters, setAppliedFilters] = useState([])

    // the free-text name term lives here too, so removing its chip can clear the input
    const [nameTerm, setNameTerm] = useState('')

    // The label is what the user sees ("Search stims"); the key is what goes to the backend,
    // camel cased via the same map the other groups use ("SearchStims"). Filter identity is
    // always the key, so the two must not be used interchangeably.
    const NAME_LABEL = props.textFieldLabel
    const NAME_GROUP_KEY = groupFor(NAME_LABEL)
    const MIN_NAME_LENGTH = 3


    const searchParams = [props.textFieldLabel, 'Stim Id', 'Brands', 'Languages', 'Countries', 'Ad Type Groups']

    function showSearch() {
        setShowTextSearch(!showTextSearch)
    }

    // Builds the request body. `filters` is the full applied set; `group` / `filter` mirror the
    // first entry so a backend reading only the single pair still applies one of them rather
    // than falling through to no filtering at all.
    function buildSearchPayload(filters) {
        const payload = {
            results_per_page: RESULTS_PER_PAGE,
            page_num: 1,
            filters: filters.map(({ group, filter }) => ({ group, filter })),
        }

        return payload
    }

    function runSearch(filters) {
        createdFilteredStimList(buildSearchPayload(filters))
    }

    function applyFilter(next) {
        // One filter per group. The backend ANDs its clauses together, so two brands could
        // never both match - a second choice for the same group replaces the first.
        const updated = [...appliedFilters.filter((applied) => applied.group !== next.group), next]

        setAppliedFilters(updated)
        runSearch(updated)
    }

    function removeFilter(group) {
        const updated = appliedFilters.filter((applied) => applied.group !== group)

        if (group === NAME_GROUP_KEY) {
            setNameTerm('')
        }

        setAppliedFilters(updated)
        // an empty set searches unfiltered, which is what "no filters applied" should show
        runSearch(updated)
    }

    function clearFilters() {
        setNameTerm('')
        setAppliedFilters([])
        runSearch([])
    }

    // The name search goes through the same applied set as every other filter, so a name and
    // a brand combine instead of the last one used replacing the other. It stays live rather
    // than needing Enter - a LIKE match is useful as you type, unlike an exact id lookup.
    function handleNameChange(term) {
        setNameTerm(term)

        // kept so the redux search term stays in step for anything else reading it
        if (textFieldChange) {
            textFieldChange(term, NAME_LABEL)
        }

        const trimmed = term.trim()

        if (trimmed.length >= MIN_NAME_LENGTH) {
            applyFilter({
                searchParam: NAME_LABEL,
                group: NAME_GROUP_KEY,
                filter: trimmed,
                label: trimmed,
            })
            return
        }

        // below the threshold there is nothing to search on, so drop the filter - but only
        // if one was applied, otherwise every keystroke would re-run the search
        if (appliedFilters.some((applied) => applied.group === NAME_GROUP_KEY)) {
            removeFilter(NAME_GROUP_KEY)
        }
    }

    // fieldOptions holds { value, label } - value is what gets sent to the stim search
    // as `filter`, label is what the dropdown shows. Brands/languages/countries filter
    // by name, so value and label match; ad type groups filter by id.
    async function getLanguages() {
        setFieldOptions([])
        const res = await getStimLanguages()
        res.data.forEach(language => {
            setFieldOptions((prev) => [...prev, { value: language.name, label: language.name }])
        });
    }

    async function getBrands() {
        setFieldOptions([])
        const res = await getStimBrands()
        res.data.forEach(brand => {
            setFieldOptions((prev) => [...prev, { value: brand.name, label: brand.name }])
        });
    }

    async function getCountries() {
        setFieldOptions([])
        const res = await getStimCountries()
        res.data.forEach(country => {
            setFieldOptions((prev) => [...prev, { value: country.name, label: country.name }])
        });
    }

    async function getAdTypeGroups() {
        setFieldOptions([])
        const res = await getStimAdTypeGroups()
        if (!res) {
            return
        }
        res.data.forEach(adTypeGroup => {
            setFieldOptions((prev) => [...prev, { value: adTypeGroup.id, label: adTypeGroup.name }])
        });
    }

    useEffect(() => {
        getBrands()
        getLanguages()
        getCountries()
    }, [])

    function handleSearchParamsChange(event) {

        setSearchOption(event.target.value)

        switch (event.target.value) {
            case "Brands":
                getBrands()
                break;
            case "Languages":
                getLanguages()
                break;
            case "Countries":
                getCountries()
                break;
            case "Ad Type Groups":
                getAdTypeGroups()
                break;
            case "Stim Id":
                // a free-text id lookup, so there is no option list to fetch - clear any
                // options left over from the previous search type
                setFieldOptions([])
                break;
            default:
                break;
        }
    }

    return (
        <div style={{ width: "100%", padding: "0px 16px 16px 16px", display: "flex", flexDirection: "column", gap: 12 }}>

            <div style={{ display: "flex", justifyContent: props.button ? "space-between" : "flex-start", alignItems: "center" }}>

                <InputLabel id="demo-simple-select-label">Search Options</InputLabel>
                <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={''}
                    label="Search Options"
                    onChange={handleSearchParamsChange}
                >
                    {
                        searchParams.map((param, i) => {
                            return <MenuItem value={param} key={i}>{param}</MenuItem>
                        })
                    }
                </Select>

                <SelectedSearchOption
                    searchParam={searchOptions}
                    textFieldLabel={props.textFieldLabel}
                    nameTerm={nameTerm}
                    onNameChange={handleNameChange}
                    fieldOptions={fieldOptions}
                    searchOptions={searchOptions}
                    onApplyFilter={applyFilter}
                />
            </div>

            {/* applied filters, so it is always visible which criteria the results reflect.
                Each is removable, and removing one re-runs the search with the rest. */}
            {appliedFilters.length ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "rgb(99, 115, 129)" }}>filters</span>

                    {appliedFilters.map((applied) => (
                        <Chip
                            key={applied.group}
                            size="small"
                            label={`${applied.searchParam}: ${applied.label}`}
                            title={`${applied.searchParam}: ${applied.label}`}
                            onDelete={() => removeFilter(applied.group)}
                        />
                    ))}

                    {appliedFilters.length > 1 ? (
                        <Button size="small" onClick={clearFilters}>clear all</Button>
                    ) : null}
                </div>
            ) : null}
        </div>
    )
}
